import { useCallback, useMemo, useState } from 'react';

import { usePericlesClient } from '../core/PericlesProvider';

import { buildEntityPathSegment } from './entityPath';

/**
 * Parameters for deleting an entity record.
 */
export interface UseDeleteEntityParams {
  entity: string;
  id?: string | number;
}

/**
 * Result returned by the delete hook, including submission state.
 */
export interface UseDeleteEntityResult<TOutput> {
  remove: (idOverride?: string | number) => Promise<TOutput | undefined>;
  isSubmitting: boolean;
  isError: boolean;
  error: unknown;
  data: TOutput | undefined;
}

/**
 * Issues a DELETE request for an entity using the Pericles client configuration.
 */
export function useDeleteEntity<TOutput = unknown>(
  params: UseDeleteEntityParams
): UseDeleteEntityResult<TOutput> {
  const { baseUrl, fetcher } = usePericlesClient();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(undefined);
  const [data, setData] = useState<TOutput | undefined>(undefined);

  const base = useMemo(() => baseUrl.replace(/\/$/, ''), [baseUrl]);

  const buildUrl = useCallback(
    (idValue?: string | number) => {
      const resolvedId = idValue ?? params.id;

      if (resolvedId === undefined || resolvedId === null) {
        throw new Error('An id is required to delete an entity');
      }

      return `${base}/entities/${buildEntityPathSegment(params.entity)}/${encodeURIComponent(String(resolvedId))}`;
    },
    [base, params.entity, params.id]
  );

  const remove = useCallback(async (idOverride?: string | number) => {
    setIsSubmitting(true);
    setIsError(false);
    setError(undefined);

    try {
      const url = buildUrl(idOverride);

      const response = await fetcher(url, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      let json: TOutput | undefined;

      try {
        json = (await response.json()) as TOutput;
      } catch {
        json = undefined;
      }

      setData(json);
      return json;
    } catch (err) {
      setIsError(true);
      setError(err);
      setData(undefined);
      return undefined;
    } finally {
      setIsSubmitting(false);
    }
  }, [buildUrl, fetcher]);

  return {
    remove,
    isSubmitting,
    isError,
    error,
    data,
  };
}
